interface RedemptionSpinnerProps {
  /** Text under the spinner; also its accessible name (e.g. "Submitting your details…"). */
  label: string;
}

/**
 * Branded processing overlay — the GfG "g" mark inside a spinning arc ring, over a label.
 * Covers its nearest positioned ancestor on ≥sm (place inside a `relative` container) and
 * the full viewport on phones. Used as the recipient-details submit loading state (RFW-7).
 */
export default function RedemptionSpinner({ label }: RedemptionSpinnerProps) {
  return (
    <div className="fixed inset-0 z-40 flex flex-col items-center justify-center gap-4.5 bg-white sm:absolute sm:z-10">
      <div className="relative h-15 w-15">
        <svg
          className="absolute inset-0 h-full w-full animate-spin"
          viewBox="0 0 60 60"
          role="img"
          aria-label={label}
        >
          <circle
            cx="30"
            cy="30"
            r="26"
            fill="none"
            stroke="var(--color-clear-dust)"
            strokeWidth="3"
          />
          <circle
            cx="30"
            cy="30"
            r="26"
            fill="none"
            stroke="var(--color-dust-ink)"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray="42 400"
          />
        </svg>
        <span className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 w-3.75 leading-none">
          <svg
            viewBox="0 0 253.17 445.79"
            aria-hidden="true"
            className="block h-auto w-full [&_path]:fill-rodeo-black"
          >
            <path d="m205.21,11.42c4.57-6.85,12.18-11.42,21.7-11.42,14.83,0,26.25,11.42,26.25,26.26s-11.42,25.88-26.25,25.88c-10.28,0-18.66-5.33-22.85-13.32-2.29,3.8-4.94,9.9-6.85,14.85l-2.66-1.91c.37-11.03,2.28-28.94,10.65-40.34" />
            <path d="m8.23,366.18c10.85-21.44,29.23-21.44,38.69-12.24,15.04,14.19,7.8,39.24-18.37,44.53-11.14,2.22-27.83,6.68-27.55,17.53-3.07-19.48,1.39-38.13,7.23-49.82" />
            <path d="m9.62,123.75c0-67.93,43.98-85.45,78.49-89.07-13.63,3.62-24.21,23.39-24.21,48.71v70.71c0,14.19-.29,47.31,10.85,56.22-31.74-8.9-65.13-27.55-65.13-86.56" />
            <path d="m141.14,210.3c11.13-8.9,10.85-42.02,10.85-56.22v-70.7c0-25.32-10.57-45.09-24.21-48.71,34.51,3.62,78.49,21.15,78.49,89.07,0,59.01-33.4,77.65-65.13,86.56" />
            <path d="m198.07,385.94c-48.43-30.05-91.31-54.55-147.53-87.4-9.47-5.56-12.79-11.7-5.84-22.82l35.36-50.93c-5.02,8.07.82,18.09,17.25,27.55,31.74,18.37,50.39,28.4,95.48,53.44,50.93,28.4,64.3,85.45,44.53,140.01,3.07-20.59-18.65-47.05-39.24-59.85" />
          </svg>
        </span>
      </div>
      <div className="font-lato text-[14px] tracking-[0.3px] text-graphite">{label}</div>
    </div>
  );
}
